/**
 * Remontées : gares et pylônes (instanciés, statiques), câbles (segments de
 * ligne) et véhicules animés. La position de chaque siège se déduit du câble
 * s(t) transmis par le serveur : client et serveur voient le même siège au
 * même endroit, sans flux de positions.
 */
import * as THREE from 'three/webgpu';
import { LIFTS, cableAt, sampleLoop, type Heightfield, type Lift, type LiftGeometry } from '@snowchain/shared';
import { InstancedLodSet, composeYawRoll, linear, type Frustum } from './instancing';
import type { AssetLibrary } from './assets';
import { LOD_DISTANCES } from './lod';
import type { ClientStore } from '../state/store';

/** Teintes des sièges et des gares par remontée (index `Lift.color`). */
export const LIFT_COLORS = [0xd96e30, 0x4e8ca8, 0x4e7a5a, 0xb8432f, 0xcda97c, 0x2e5d74];

interface StaticItem {
  set: InstancedLodSet;
  x: number;
  y: number;
  z: number;
  r: number;
  m: Float32Array;
  /** Teinte linéaire (couleur de la ligne pour les gares, blanc pour les pylônes). */
  tint: [number, number, number];
}

export class LiftsLayer {
  readonly group = new THREE.Group();
  private sets: Record<string, InstancedLodSet> = {};
  private statics: StaticItem[] = [];
  private cables: THREE.LineSegments;
  private tmp = new Float64Array(5);
  private mat = new Float32Array(16);
  carriersDrawn = 0;

  constructor(
    private readonly lib: AssetLibrary,
    private readonly hf: Heightfield,
  ) {
    this.group.name = 'lifts';
    const mk = (name: string, cap: number, cat: string, dynamic = false) => {
      this.sets[name] = new InstancedLodSet(name, this.group, lib.get(name).geometries, lib.instanced, cap, LOD_DISTANCES[cat] as number[], { shadowLods: 1, dynamic });
    };
    mk('lift_station', 32, 'station');
    mk('drag_station', 32, 'station');
    mk('pylon', 256, 'pylon');
    mk('drag_pylon', 256, 'pylon');
    mk('chair_4', 1024, 'carrier', true);
    mk('gondola_cabin', 512, 'carrier', true);
    mk('drag_hanger', 512, 'carrier', true);
    const mat = new THREE.LineBasicNodeMaterial({ color: 0x2a3640 });
    this.cables = new THREE.LineSegments(new THREE.BufferGeometry(), mat);
    this.cables.frustumCulled = false;
    this.cables.name = 'cables';
    this.group.add(this.cables);
  }

  /** Gares, pylônes et câbles : reconstruits quand les remontées changent. */
  rebuild(store: ClientStore): void {
    const replica = store.replica;
    if (!replica) return;
    this.statics = [];
    const segs: number[] = [];
    for (const l of replica.lifts.values()) {
      const geo = store.liftGeometry(l.id);
      if (!geo) continue;
      const spec = LIFTS[l.kind];
      const station = this.sets[spec.assets.station] as InstancedLodSet;
      const pylon = this.sets[spec.assets.pylon] as InstancedLodSet;
      const building = l.status === 'construction';
      const grow = building ? 0.25 + 0.75 * l.progress : 1;
      const stationR = this.lib.get(spec.assets.station).bounds.radius;
      const white: [number, number, number] = [1, 1, 1];
      // Les gares portent la couleur de la ligne (cases teintées de l'atlas), comme les sièges.
      const lineTint = linear(LIFT_COLORS[l.color % LIFT_COLORS.length] as number);
      const addStatic = (set: InstancedLodSet, x: number, y: number, z: number, yaw: number, sy: number, r: number, tint: [number, number, number] = white) => {
        const m = new Float32Array(16);
        composeYawRoll(m, 0, x, y, z, yaw, 0, 1, sy, 1);
        this.statics.push({ set, x, y, z, r, m, tint });
      };
      addStatic(station, l.bottom[0], geo.groundBottom - 0.2, l.bottom[1], geo.heading, grow, stationR, lineTint);
      addStatic(station, l.top[0], geo.groundTop - 0.2, l.top[1], geo.heading + Math.PI, grow, stationR, lineTint);
      const shown = building ? Math.floor(geo.pylons.length * l.progress) : geo.pylons.length;
      const pr = this.lib.get(spec.assets.pylon).bounds.radius;
      for (let i = 0; i < shown; i++) {
        const p = geo.pylons[i]!;
        // Les pylônes d'un téléski ont leur câble à la hauteur nominale de l'asset.
        addStatic(pylon, p.x, p.ground, p.z, geo.heading, 1, pr);
      }
      if (!building) {
        for (const path of [geo.up, geo.down]) {
          for (let i = 0; i < path.xs.length - 1; i++) {
            segs.push(path.xs[i]!, path.ys[i]!, path.zs[i]!, path.xs[i + 1]!, path.ys[i + 1]!, path.zs[i + 1]!);
          }
        }
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(segs), 3));
    this.cables.geometry.dispose();
    this.cables.geometry = g;
  }

  updateStatic(camera: THREE.Camera, frustum: Frustum): void {
    const cp = camera.position;
    for (const name of ['lift_station', 'drag_station', 'pylon', 'drag_pylon']) this.sets[name]!.begin();
    for (const s of this.statics) {
      if (!frustum.sphere(s.x, s.y + s.r * 0.5, s.z, s.r)) continue;
      s.set.add(s.m, 0, Math.hypot(s.x - cp.x, s.y - cp.y, s.z - cp.z), s.tint[0], s.tint[1], s.tint[2]);
    }
    for (const name of ['lift_station', 'drag_station', 'pylon', 'drag_pylon']) this.sets[name]!.end();
  }

  /** Véhicules : chaque image, à partir de la position du câble. */
  updateCarriers(store: ClientStore, t: number, camera: THREE.Camera, frustum: Frustum): void {
    const replica = store.replica;
    const cp = camera.position;
    for (const name of ['chair_4', 'gondola_cabin', 'drag_hanger']) this.sets[name]!.begin();
    let n = 0;
    if (replica) {
      for (const l of replica.lifts.values()) {
        if (l.status === 'construction') continue;
        const geo = store.liftGeometry(l.id);
        if (!geo) continue;
        n += this.drawLift(l, geo, t, cp, frustum);
      }
    }
    for (const name of ['chair_4', 'gondola_cabin', 'drag_hanger']) this.sets[name]!.end();
    this.carriersDrawn = n;
  }

  private drawLift(l: Lift, geo: LiftGeometry, t: number, cp: THREE.Vector3, frustum: Frustum): number {
    const spec = LIFTS[l.kind];
    const set = this.sets[spec.assets.carrier] as InstancedLodSet;
    const [r, g, b] = linear(LIFT_COLORS[l.color % LIFT_COLORS.length] as number);
    const s = cableAt(l.cable, t);
    const out = this.tmp;
    const drag = l.kind === 'teleski';
    let n = 0;
    for (let k = 0; k < geo.carriers; k++) {
      const up = sampleLoop(geo, s + k * geo.spacing, out);
      const x = out[0] as number;
      const y = out[1] as number;
      const z = out[2] as number;
      if (!frustum.sphere(x, y - 1.5, z, 3)) continue;
      const d = Math.hypot(x - cp.x, y - cp.y, z - cp.z);
      const yaw = Math.atan2(out[3] as number, out[4] as number);
      let sy = 1;
      if (drag && up) {
        // La perche descend jusqu'à la hanche du skieur tiré au sol.
        const ground = this.hf.height(x, z);
        sy = Math.max(0.6, (y - ground - 1.05) / 2.4);
      }
      composeYawRoll(this.mat, 0, x, y, z, yaw, 0, 1, sy, 1);
      if (set.add(this.mat, 0, d, r, g, b)) n++;
    }
    return n;
  }

  stats(): { instances: number; triangles: number } {
    let instances = 0;
    let triangles = 0;
    for (const s of Object.values(this.sets)) {
      instances += s.instanceCount;
      triangles += s.triangleCount;
    }
    return { instances, triangles };
  }
}
